// Hero graphic: field signals flow into an Ignition gateway, through the
// Coprocessor module (sync → window → compute), and back out as derived tags.
// Particles ride the paths via CSS motion-path (offset-path).

type Source = { y: number; label: string; sub: string; color: string; trace: string };

const SOURCES: Source[] = [
  { y: 110, label: "PLC", sub: "pressure · flow", color: "var(--mk-cyan)", trace: "M0 0c8-8 16 6 24-2s14-10 22 0 16 6 24-4 16 8 24 6" },
  { y: 220, label: "VFD", sub: "motor current", color: "var(--mk-amber)", trace: "M0 0h14l6-12 8 22 6-10h20l6 8 8-16 6 8h20" },
  { y: 330, label: "SENSOR", sub: "vibration", color: "var(--mk-steel)", trace: "M0 0l6-6 6 10 6-12 6 14 6-10 6 6 6-8 6 12 6-12 6 8 6-6 6 10 6-12 6 8 6-4 4 2" },
  { y: 440, label: "METER", sub: "energy · kWh", color: "var(--mk-green)", trace: "M0 8l16-1 16-2 16-2 16-1 16-1 14-1" },
];

function sourcePath(y: number): string {
  // From the source node's right edge to the gateway's left inlet (x=470,y=275)
  return `M232 ${y} C 330 ${y}, 360 275, 470 275`;
}

const OUTLETS = [
  { y: 150, label: "Derived tags", sub: "back in the tag tree" },
  { y: 275, label: "Alarms & events", sub: "clean inputs for rules" },
  { y: 400, label: "Perspective · Vision", sub: "values on screens" },
];

function outletPath(y: number): string {
  return `M840 275 C 930 275, 950 ${y}, 1030 ${y}`;
}

export function FlowHero() {
  return (
    <svg
      className="mk-svg"
      viewBox="0 0 1240 560"
      role="img"
      aria-label="Live signals from PLCs, drives, sensors and meters flow into an Ignition gateway, where the Coprocessor module synchronizes, windows and computes them, and writes derived tags, alarms and screen values back out."
    >
      <defs>
        <linearGradient id="fh-gw" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="rgba(255,255,255,.06)" />
          <stop offset="1" stopColor="rgba(255,255,255,.015)" />
        </linearGradient>
        <linearGradient id="fh-mod" x1="0" x2="1">
          <stop offset="0" stopColor="rgba(79,216,236,.14)" />
          <stop offset="1" stopColor="rgba(79,216,236,.04)" />
        </linearGradient>
      </defs>

      {/* Column labels */}
      <text x="20" y="42" className="svg-k">Field · live signals</text>
      <text x="470" y="42" className="svg-k">Ignition gateway</text>
      <text x="1030" y="42" className="svg-k">Back to Ignition</text>

      {/* Source → gateway pipes */}
      {SOURCES.map((s) => (
        <g key={s.label}>
          <path d={sourcePath(s.y)} className="pipe" />
          <path d={sourcePath(s.y)} className="pipe-lit" />
        </g>
      ))}
      {/* Gateway → outlet pipes */}
      {OUTLETS.map((o) => (
        <g key={o.label}>
          <path d={outletPath(o.y)} className="pipe" />
          <path d={outletPath(o.y)} className="pipe-out" />
        </g>
      ))}

      {/* Source nodes */}
      {SOURCES.map((s, i) => (
        <g key={s.label} transform={`translate(20 ${s.y - 34})`}>
          <rect width="212" height="68" rx="2" className="node" />
          <rect x="0" y="0" width="4" height="68" rx="2" fill={s.color} />
          <text x="18" y="27" className="svg-l">{s.label}</text>
          <text x="18" y="50" className="svg-s">{s.sub}</text>
          <g transform="translate(118 34)">
            <path d={s.trace} fill="none" stroke={s.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="draw" style={{ ["--delay" as string]: `${i * 0.25}s` }} pathLength={1} />
          </g>
        </g>
      ))}

      {/* Gateway */}
      <rect x="470" y="70" width="370" height="420" rx="2" fill="url(#fh-gw)" stroke="rgba(255,255,255,.22)" strokeWidth="1.5" />
      <text x="500" y="106" className="svg-l" style={{ fontSize: 17 }}>Ignition Gateway</text>
      <text x="500" y="128" className="svg-s">tags · history · alarms · Perspective</text>

      {/* Coprocessor module inside gateway */}
      <rect x="494" y="152" width="322" height="310" rx="2" fill="url(#fh-mod)" stroke="var(--mk-cyan)" strokeWidth="1.5" />
      <g transform="translate(514 176)">
        <rect width="26" height="26" rx="2" fill="#2D3449" stroke="rgba(175,198,255,.4)" />
        <path d="M8 6v2M13 6v2M18 6v2M8 18v2M13 18v2M18 18v2M6 8h2M6 13h2M6 18h2M18 8h2M18 13h2M18 18h2" stroke="#AFC6FF" strokeWidth="1.6" strokeLinecap="round" />
        <rect x="8" y="8" width="10" height="10" rx="2" stroke="#AFC6FF" strokeWidth="1.6" fill="none" />
      </g>
      <text x="552" y="195" className="svg-l" style={{ fontSize: 17 }}>Coprocessor module</text>
      <text x="514" y="226" className="svg-s">native C++ engine · SQL</text>

      {/* Stages */}
      {[
        { x: 508, t: "SYNC", s: "align clocks" },
        { x: 609, t: "WINDOW", s: "moving · keyed" },
        { x: 710, t: "COMPUTE", s: "SQL operators" },
      ].map((st, i) => (
        <g key={st.t} transform={`translate(${st.x} 250)`}>
          <rect width="92" height="70" rx="2" className="stage" />
          <text x="46" y="30" textAnchor="middle" className="svg-l" style={{ fontSize: 13, letterSpacing: ".08em" }}>{st.t}</text>
          <text x="46" y="52" textAnchor="middle" className="svg-s" style={{ fontSize: 9.5 }}>{st.s}</text>
          {i < 2 && <path d="M92 35h9" stroke="var(--mk-cyan)" strokeWidth="2" strokeLinecap="round" />}
          {i < 2 && <path d="M97 31l4 4-4 4" fill="none" stroke="var(--mk-cyan)" strokeWidth="2" strokeLinecap="round" />}
        </g>
      ))}

      {/* SQL snippet */}
      <g transform="translate(514 344)">
        <rect width="282" height="96" rx="2" fill="rgba(0,0,0,.35)" stroke="rgba(255,255,255,.08)" />
        <text x="14" y="26" className="svg-s" style={{ fontSize: 12.5 }}>
          <tspan fill="#F2B84B">CREATE STREAM</tspan> <tspan fill="#F4F3EE">pump_health</tspan> <tspan fill="#F2B84B">AS</tspan>
        </text>
        <text x="14" y="46" className="svg-s" style={{ fontSize: 12.5 }}>
          <tspan fill="#F2B84B">SELECT</tspan> <tspan fill="#4FD8EC">avg</tspan><tspan fill="#F4F3EE">(current)</tspan> <tspan fill="#F2B84B">OVER</tspan> <tspan fill="#F4F3EE">30s,</tspan>
        </text>
        <text x="14" y="66" className="svg-s" style={{ fontSize: 12.5 }}>
          <tspan fill="#F4F3EE">       </tspan><tspan fill="#4FD8EC">stddev</tspan><tspan fill="#F4F3EE">(vibration)</tspan> <tspan fill="#F2B84B">OVER</tspan> <tspan fill="#F4F3EE">30s</tspan>
        </text>
        <text x="14" y="86" className="svg-s" style={{ fontSize: 12.5 }}>
          <tspan fill="#F2B84B">FROM</tspan> <tspan fill="#F4F3EE">stream</tspan> <tspan fill="#F2B84B">GROUP BY</tspan> <tspan fill="#F4F3EE">{"{pump}"}</tspan>
        </text>
      </g>

      {/* Outlet nodes */}
      {OUTLETS.map((o) => (
        <g key={o.label} transform={`translate(1030 ${o.y - 34})`}>
          <rect width="200" height="68" rx="2" className="node" />
          <rect x="0" y="0" width="4" height="68" rx="2" fill="var(--mk-green)" />
          <text x="18" y="27" className="svg-l" style={{ fontSize: 14 }}>{o.label}</text>
          <text x="18" y="50" className="svg-s" style={{ fontSize: 12 }}>{o.sub}</text>
        </g>
      ))}

      {/* Particles */}
      {SOURCES.map((s, i) =>
        [0, 1].map((k) => (
          <circle
            key={`${s.label}-${k}`}
            r="4.5"
            className="particle"
            style={{
              offsetPath: `path('${sourcePath(s.y)}')`,
              ["--dur" as string]: "3.6s",
              ["--delay" as string]: `${i * 0.45 + k * 1.8}s`,
            }}
          />
        )),
      )}
      {OUTLETS.map((o, i) =>
        [0, 1].map((k) => (
          <circle
            key={`${o.label}-${k}`}
            r="4.5"
            className="particle-out"
            style={{
              offsetPath: `path('${outletPath(o.y)}')`,
              ["--dur" as string]: "3.2s",
              ["--delay" as string]: `${0.9 + i * 0.5 + k * 1.6}s`,
            }}
          />
        )),
      )}

      {/* Heartbeat inside the module */}
      <circle cx="798" cy="222" r="5" className="svg-green pulse" />
      <text x="788" y="226" textAnchor="end" className="svg-s" style={{ fontSize: 12 }}>running</text>
    </svg>
  );
}
